import React, { useEffect, useState } from 'react';
import { api, type Receipt } from '../lib/api';
import { T, sx } from '../lib/tokens';
import { VendorTile } from '../components/ui/VendorTile';
import { Button } from '../components/ui/Button';
import { Check, Arrow, Upload, X } from '../components/ui/Icons';

const REVIEW_STATUSES = new Set(['pending', 'parsed', 'needs_review', 'confirmed', 'failed', 'duplicate']);

function fmtDate(d: string | null): string {
  if (!d) return '--';
  const dt = new Date(d);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
}

function fmtMoney(amount: number | null, currency: string): string {
  if (amount == null) return '--';
  return `${currency} ${amount.toFixed(2)}`;
}

function reviewReason(r: Receipt): string {
  if (r.status === 'failed') return r.error_message ?? 'Processing failed. Please re-upload or enter manually.';
  if (r.status === 'duplicate') return 'This looks like a duplicate of an existing receipt.';
  if (r.status === 'pending') return 'Still processing. Check back shortly.';
  if (r.status === 'needs_review') return 'We weren\'t confident enough to auto-categorise this. Pick the right account below and we\'ll remember for next time.';
  if (!r.vendor) return 'Could not extract the vendor name from this receipt.';
  if (!r.amount) return 'Could not determine the amount. Please enter it manually.';
  if (!r.account_code) return 'No matching account code found. Please select one.';
  if (r.confidence != null && r.confidence < 0.7) return `Low extraction confidence (${(r.confidence * 100).toFixed(0)}%). Please verify the details.`;
  return 'Needs manual review before posting to your books.';
}

export function ReviewInbox() {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [accounts, setAccounts] = useState<Array<{ AccountID: string; Code: string; Name: string; Type: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState<Set<string>>(new Set());
  const [selectedAccounts, setSelectedAccounts] = useState<Record<string, string>>({});

  async function load() {
    const [r, a] = await Promise.all([api.listReceipts(), api.xeroAccounts()]);
    setReceipts(r.receipts.filter((rc: Receipt) => REVIEW_STATUSES.has(rc.status)));
    setAccounts(a.accounts.filter((ac) => ac.Type === 'EXPENSE' || ac.Type === 'DIRECTCOSTS'));
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  async function approveAndPost(r: Receipt) {
    setPosting((s) => new Set(s).add(r.id));
    try {
      const accountCode = selectedAccounts[r.id] || r.account_code;
      if (accountCode && accountCode !== r.account_code) {
        await api.confirmReceipt(r.id, { account_code: accountCode });
      } else if (r.status === 'parsed') {
        await api.confirmReceipt(r.id, {});
      }
      await api.postReceipt(r.id);
      setReceipts((prev) => prev.filter((rc) => rc.id !== r.id));
    } catch {
      const fresh = await api.getReceipt(r.id);
      setReceipts((prev) => prev.map((rc) => (rc.id === r.id ? fresh.receipt : rc)));
    } finally {
      setPosting((s) => { const n = new Set(s); n.delete(r.id); return n; });
    }
  }

  async function deleteReceipt(id: string) {
    if (!confirm('Delete this receipt?')) return;
    await api.deleteReceipt(id);
    setReceipts((prev) => prev.filter((r) => r.id !== id));
  }

  async function retryReceipt(id: string) {
    await api.retryReceipt(id);
    const fresh = await api.getReceipt(id);
    setReceipts((prev) => prev.map((r) => (r.id === id ? fresh.receipt : r)));
  }

  if (loading) {
    return <div style={{ padding: 40, color: T.graphiteDim, fontSize: 14 }}>Loading...</div>;
  }

  // Common expense accounts for quick-pick pills
  const topAccounts = accounts.slice(0, 6);

  return (
    <div>
      {/* Back link */}
      <a
        href="#/"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 13,
          color: T.graphiteDim,
          textDecoration: 'none',
          marginBottom: 16,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = T.graphite; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = T.graphiteDim; }}
      >
        <Arrow size={14} style={{ transform: 'rotate(180deg)' }} />
        All receipts
      </a>

      {/* Heading */}
      <h1 style={{ ...sx.serif, fontSize: 32, color: T.graphite, marginBottom: 8 }}>
        {receipts.length > 0 ? `${receipts.length} ${receipts.length === 1 ? 'receipt needs' : 'receipts need'} review` : 'Inbox zero.'}
      </h1>

      {/* Empty state */}
      {receipts.length === 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '64px 0',
            gap: 16,
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: T.sage,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Check size={32} color={T.navy} />
          </div>
          <p style={{ fontSize: 15, color: T.graphiteMuted, textAlign: 'center', maxWidth: 320, lineHeight: 1.5 }}>
            Everything is posted and reconciled. Nice work. Go grab a coffee.
          </p>
        </div>
      )}

      {/* Review cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
        {receipts.map((r) => {
          const isFailed = r.status === 'failed';
          const isPostingThis = posting.has(r.id);
          const chosen = selectedAccounts[r.id] || r.account_code || '';

          return (
            <div
              key={r.id}
              style={{
                background: '#FFFFFF',
                border: `1px solid ${T.paperLine}`,
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              {/* Card header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '16px 20px',
                  borderBottom: `1px solid ${T.paperLine}`,
                }}
              >
                <VendorTile vendor={r.vendor ?? '?'} size={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.graphite }}>
                    {r.vendor ?? '(unknown vendor)'}
                  </div>
                  <div style={{ fontSize: 13, color: T.graphiteDim }}>{fmtDate(r.receipt_date)}</div>
                </div>
                <div style={{ ...sx.mono, fontSize: 18, fontWeight: 600, color: T.graphite }}>
                  {fmtMoney(r.amount, r.currency)}
                </div>
              </div>

              {/* Review reason */}
              <div
                style={{
                  padding: '12px 20px',
                  background: '#FDF9EC',
                  borderBottom: `1px solid ${T.paperLine}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: isFailed ? T.fail : T.warn,
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 13, color: isFailed ? T.fail : T.warn, lineHeight: 1.5 }}>
                  {reviewReason(r)}
                </span>
              </div>

              {/* Actions */}
              <div style={{ padding: '16px 20px' }}>
                {isFailed ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button variant="navy" size="small" onClick={() => retryReceipt(r.id)}>
                      Re-process
                    </Button>
                    <Button variant="ghostLight" size="small" onClick={() => { window.location.hash = `#/receipts/${r.id}`; }}>
                      Edit manually
                    </Button>
                    <div style={{ flex: 1 }} />
                    <Button variant="delete" size="small" onClick={() => deleteReceipt(r.id)}>
                      <X size={13} /> Delete
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Account pill selector */}
                    {accounts.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <p style={{ ...sx.labelLight, marginBottom: 8 }}>Account</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {topAccounts.map((a) => {
                            const active = chosen === a.Code;
                            return (
                              <button
                                key={a.AccountID}
                                onClick={() => {
                                  setSelectedAccounts((prev) => ({ ...prev, [r.id]: a.Code }));
                                }}
                                style={{
                                  padding: '5px 14px',
                                  fontSize: 12,
                                  fontFamily: "'Geist', sans-serif",
                                  fontWeight: 500,
                                  borderRadius: 100,
                                  border: `1px solid ${active ? T.sage : T.paperLine}`,
                                  background: active ? T.sage : 'transparent',
                                  color: active ? T.navy : T.graphiteMuted,
                                  cursor: 'pointer',
                                  transition: 'all 150ms ease',
                                }}
                              >
                                {a.Code} - {a.Name}
                              </button>
                            );
                          })}
                          {accounts.length > 6 && (
                            <button
                              onClick={() => { window.location.hash = `#/receipts/${r.id}`; }}
                              style={{
                                padding: '5px 14px',
                                fontSize: 12,
                                fontFamily: "'Geist', sans-serif",
                                fontWeight: 500,
                                borderRadius: 100,
                                border: `1px solid ${T.paperLine}`,
                                background: 'transparent',
                                color: T.graphiteDim,
                                cursor: 'pointer',
                              }}
                            >
                              More...
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Button
                        variant="sage"
                        size="small"
                        onClick={() => approveAndPost(r)}
                        disabled={isPostingThis || r.status === 'pending'}
                      >
                        <Check size={14} />
                        {isPostingThis ? 'Posting...' : 'Approve & post'}
                      </Button>
                      <Button variant="ghostLight" size="small" onClick={() => { window.location.hash = `#/receipts/${r.id}`; }}>
                        Edit details
                      </Button>
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={() => deleteReceipt(r.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                          color: T.graphiteDim,
                          opacity: 0.5,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = T.fail; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = T.graphiteDim; }}
                        title="Delete"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
